import { t } from "@lingui/macro";
import { ChatCircle, FileText, Gear, Lock, SidebarSimple, SignOut, Sparkle } from "@phosphor-icons/react";
import { useBreakpoint } from "@reactive-resume/hooks";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { useNavigate } from "react-router-dom";

import { useCoverLetterStore } from "@/client/components/cover-letter-dialog";
import { Icon } from "@/client/components/icon";
import { ThemeSwitch } from "@/client/components/theme-switch";
import { UserAvatar } from "@/client/components/user-avatar";
import { useLogout } from "@/client/services/auth";
import { useAiStore } from "@/client/stores/ai";
import { useBuilderStore } from "@/client/stores/builder";
import { useResumeStore } from "@/client/stores/resume";

/* eslint-disable lingui/no-unlocalized-strings */

export const BuilderHeader = () => {
  const navigate = useNavigate();
  const { logout } = useLogout();
  const { isDesktop } = useBreakpoint();

  const title = useResumeStore((state) => state.resume.title);
  const locked = useResumeStore((state) => state.resume.locked);
  const saveStatus = useResumeStore((state) => state.saveStatus);
  const toggle = useBuilderStore((state) => state.toggle);
  const toggleAiPanel = useAiStore((state) => state.togglePanel);
  const aiPanelOpen = useAiStore((state) => state.panelOpen);
  const openCoverLetter = useCoverLetterStore((state) => state.openDialog);

  return (
    <header className="flex h-12 min-w-0 w-full shrink-0 items-center overflow-hidden border-b bg-background px-4">
      {/* Left zone: logo (desktop) or sidebar toggle (mobile) */}
      <div className="flex min-w-0 flex-1 items-center gap-x-2">
        {!isDesktop && (
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 shrink-0"
            onClick={() => toggle("left")}
          >
            <SidebarSimple size={16} />
          </Button>
        )}

        {isDesktop && (
          <>
            <button
              onClick={() => navigate("/dashboard")}
              className="flex shrink-0 items-center gap-x-2"
            >
              <Icon size={18} />
              <span className="text-sm font-semibold tracking-tight">
                {/* eslint-disable-next-line lingui/no-unlocalized-strings */}
                {"CareerBldr"}
              </span>
            </button>

          </>
        )}
      </div>

      {/* Center: resume title */}
      <div className="flex items-center gap-x-1.5">
        <h1 className="max-w-[200px] truncate text-sm font-medium sm:max-w-xs">{title}</h1>
        {locked && (
          <Tooltip content={t`This resume is locked, please unlock to make further changes.`}>
            <Lock size={12} className="opacity-60" />
          </Tooltip>
        )}
        {saveStatus === "error" && (
          <Tooltip content={t`Your latest change didn't save. Edit again and we'll try once more.`}>
            <span className="text-xs text-foreground/60">{t`Not saved`}</span>
          </Tooltip>
        )}
      </div>

      {/* Right zone: AI → theme → settings → user (no panel toggles) */}
      <div className="flex min-w-0 flex-1 items-center justify-end gap-x-2">
        {!isDesktop && (
          <>
            <Tooltip content={aiPanelOpen ? t`Close AI Assistant` : t`AI Assistant`}>
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  "ai-btn h-8 w-8 shrink-0 rounded-lg",
                  aiPanelOpen && "ai-btn-active",
                )}
                onClick={toggleAiPanel}
              >
                <Sparkle size={16} weight={aiPanelOpen ? "fill" : "duotone"} className="ai-btn-sparkle" />
              </Button>
            </Tooltip>
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 shrink-0"
              onClick={() => toggle("right")}
            >
              <SidebarSimple size={16} className="-scale-x-100" />
            </Button>
          </>
        )}

        {isDesktop && (
          <>
            <Tooltip content={aiPanelOpen ? t`Close AI Assistant` : t`AI Assistant`}>
              <Button
                variant="ghost"
                size="md"
                className={cn(
                  "ai-btn h-10 gap-x-2.5 rounded-xl font-semibold",
                  aiPanelOpen && "ai-btn-active",
                )}
                onClick={toggleAiPanel}
              >
                <Sparkle size={18} weight={aiPanelOpen ? "fill" : "duotone"} className="ai-btn-sparkle" />
                <span>{t`AI Assistant`}</span>
              </Button>
            </Tooltip>

            <Tooltip content={t`Cover Letter`}>
              <Button
                variant="ghost"
                size="sm"
                className="gap-x-1.5 text-xs"
                onClick={openCoverLetter}
              >
                <FileText size={14} weight="duotone" />
                <span>{t`Cover Letter`}</span>
              </Button>
            </Tooltip>

            <div className="mx-0.5 h-6 w-px bg-border" />

            <ThemeSwitch size={16} variant="outline" />

            <Button
              variant="outline"
              size="sm"
              className="gap-x-1.5 text-xs"
              onClick={() => navigate("/dashboard/settings")}
            >
              <Gear size={14} />
              <span>{t`Settings`}</span>
            </Button>

            <div className="mx-0.5 h-5 w-px bg-border" />
          </>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="shrink-0 rounded-full ring-2 ring-border transition-all hover:ring-primary/50">
              <UserAvatar size={28} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="bottom" align="end" className="w-44">
            {!isDesktop && (
              <DropdownMenuItem onClick={() => navigate("/dashboard/settings")}>
                <Gear size={14} />
                <span className="ml-2">{t`Settings`}</span>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => navigate("/contact")}>
              <ChatCircle size={14} />
              <span className="ml-2">{t`Contact Us`}</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => logout()}>
              <SignOut size={14} />
              <span className="ml-2">{t`Logout`}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};
